import React from 'react';
import { Row, Col, Divider } from 'antd';
import { styled } from 'styled-components';
import OpeningSection from 'components/OpeningSection';
import FirstArticlePromotedSection from 'components/FirstArticlePromotedSection';
import { Rectangle, HalfPage, ExpandNavibox, Wideboard, SkyscraperMobile } from 'components/Ads';
import Newsletter from 'containers/Newsletter';
import PromotedSection from 'components/Promoted';
import CardGrid from 'components/CardGrid';
import parseCategoryUrl from 'lib/parseCategoryUrl';
import Tags from 'components/Tags';
import AppSmallLogotypeIcon from 'components/Icons/AppSmallLogotype';
import Content from 'layout/Basic/Content';
import Sider from 'layout/Basic/Sider';
import GalleryMainHitbox from 'components/GalleryHitbox/MainHitbox';
import VideoMainHitbox from 'components/VideoHitbox/MainHitbox';
import { SectionTitle } from 'components/Section';
import Section from 'components/Section';
import ContentProvidersSection from 'components/ContentProvidersSection';

const Icon = styled(AppSmallLogotypeIcon)`
    vertical-align: -0.25em;
    font-size: 30px;
`;

export interface Props {
    ads: PageAds;
    articles: Paginated<Article>;
    recentArticles: Article[];
    hitboxItems: {
        articles: Article[];
        contests: Contest[];
    };
    currentCampaign: Campaign;
    paidArticles: {
        results: Article[];
    };
    companies: Company[];
    tags: Tag[];
    categoryArticles: [
        {
            category: Category;
            results: Article[];
        }
    ];
    category: Category;
    videos: Video[];
    galleries: Gallery[];
    latestIssue: Issue;
    events: Occurrence[];
    eventsPatronate: Occurrence[];
    whitePapers: WhitePaper[];
}

export const CategoryPage = ({
    category,
    hitboxItems,
    recentArticles,
    currentCampaign,
    paidArticles,
    tags,
    categoryArticles,
    galleries,
    videos,
    companies,
    whitePapers,
    ads
}: Props): JSX.Element => {
    const { rectangle, skyscraper, halfpage, navibox, wideboardBottom } = ads;
    return (
        <>
            <SectionTitle title={category.title} />
            {hitboxItems.articles.length > 0 && <OpeningSection $items={hitboxItems} />}
            <Row justify="center">
                <Col xs={24} md={12}>
                    <SkyscraperMobile id={skyscraper[0]} margin="24px auto 12px auto" />
                </Col>
                <Col xs={24} md={12}>
                    <SkyscraperMobile id={skyscraper[1]} margin="12px auto 0 auto" />
                </Col>
            </Row>
            <Section gutter={16}>
                <Content>
                    <FirstArticlePromotedSection
                        title={
                            <SectionTitle
                                title={`Najnowsze z "${category.title}"`}
                                url={`${category.seoSlug}/pokaz-wszystkie`}
                            />
                        }
                        items={recentArticles}
                    />
                </Content>
                <Sider>
                    <Section gutter={[16, 32]} justify="center">
                        <Col span={24}>
                            <Rectangle id={rectangle[0]} />
                        </Col>
                        <Col span={24}>
                            <HalfPage id={halfpage[0]} />
                        </Col>
                    </Section>
                </Sider>
            </Section>
            <Divider />
            <Section gutter={[16, 32]} justify="center">
                <Col xs={24} md={20}>
                    <Newsletter />
                </Col>
            </Section>
            <Divider />
            <Row justify="space-between" gutter={[16, 32]}>
                <Content>
                    <Row justify="center">
                        <Col>
                            <ExpandNavibox id={navibox[0]} />
                        </Col>
                    </Row>
                </Content>
                <Sider>
                    <Row>
                        <Col span={24}>
                            <Rectangle id={rectangle[1]} />
                        </Col>
                    </Row>
                </Sider>
            </Row>
            <Divider />
            {currentCampaign && currentCampaign.campaign && (
                <>
                    <Row>
                        <Col>
                            <PromotedSection campaign={currentCampaign} />
                        </Col>
                    </Row>
                    <Divider />
                </>
            )}
            <Row gutter={[16, 32]}>
                <Content>
                    {paidArticles && (
                        <CardGrid
                            type="article"
                            title={
                                <>
                                    <Icon /> Premium
                                </>
                            }
                            url="/premium"
                            items={paidArticles.results}
                            maxItems={6}
                            maxItemsInRow={3}
                            noTeaser
                            showShowMore={false}
                        />
                    )}
                </Content>
                <Sider>
                    <Section gutter={[16, 32]} justify="center">
                        <Col>
                            <Rectangle id={rectangle[2]} />
                        </Col>
                        <Col>
                            <HalfPage id={halfpage[1]} />
                        </Col>
                    </Section>
                </Sider>
            </Row>
            <Divider />
            {categoryArticles.slice(0, 1).map(({ category, results }) => (
                <>
                    <Section key={category.seoSlug}>
                        {results.length > 0 && (
                            <Col span={24}>
                                <CardGrid
                                    type="article"
                                    title={category.title}
                                    url={parseCategoryUrl(category)}
                                    items={results}
                                    noTeaser
                                />
                            </Col>
                        )}
                    </Section>
                    <Divider />
                </>
            ))}
            <Row justify="space-between" gutter={[16, 32]}>
                <Content>
                    <ExpandNavibox id={navibox[1]} />
                </Content>
                <Sider>
                    <Rectangle id={rectangle[3]} />
                </Sider>
            </Row>
            <Row>
                <Content>
                    <Row>
                        <SectionTitle title="Galeria" url="/galerie" />
                        <GalleryMainHitbox gallery={galleries[0]} />
                    </Row>

                    <Row>
                        <Col span={24} style={{ paddingTop: 45 }}>
                            <CardGrid
                                items={galleries.slice(1, -1)}
                                type="gallery"
                                url="/galeria"
                                maxItems={4}
                                maxItemsInRowMobile={2}
                            />
                        </Col>
                    </Row>
                </Content>
                <Sider>
                    <Row>
                        <Col span={24}>
                            <Rectangle id={ads.rectangle[3]} margin="35px auto 0 auto" />
                        </Col>
                        <Col span={24}>
                            <HalfPage id={ads.halfpage[2]} margin="35px auto 0 auto" />
                        </Col>
                        <Col span={24}>
                            <Rectangle id={ads.rectangle[4]} margin="35px auto 0 auto" />
                        </Col>
                    </Row>
                </Sider>
            </Row>
            {categoryArticles.slice(1, 3).map(({ category, results }) => (
                <>
                    {results.length > 0 && (
                        <>
                            <Section key={category.seoSlug}>
                                {results.length > 0 && (
                                    <Col span={24}>
                                        <CardGrid
                                            type="article"
                                            title={category.title}
                                            url={parseCategoryUrl(category)}
                                            items={results}
                                            noTeaser
                                        />
                                    </Col>
                                )}
                            </Section>
                            <Divider />
                        </>
                    )}
                </>
            ))}
            <Row style={{ margin: '24px 0 48px' }} justify="center">
                <Col sm={24} md={24} lg={24}>
                    <Wideboard id={wideboardBottom[0]} />
                </Col>
            </Row>
            <Row>
                <Col span={24}>
                    <Divider />
                </Col>
            </Row>
            {tags && (
                <Row>
                    <Col>
                        <Tags tags={tags} sameSize={false} />
                    </Col>
                    <Col span={24}>
                        <Divider />
                    </Col>
                </Row>
            )}
            <Row>
                <Content>
                    <ContentProvidersSection companies={companies} />
                </Content>

                <Sider>
                    <Section justify="center">
                        <Col span={24}>
                            <Rectangle id={rectangle[5]} />
                        </Col>
                    </Section>
                </Sider>
            </Row>
            {categoryArticles.slice(3).map(({ category, results }) => (
                <>
                    {results.length > 0 && (
                        <>
                            <Section key={category.seoSlug}>
                                {results.length > 0 && (
                                    <Col span={24}>
                                        <CardGrid
                                            type="article"
                                            title={category.title}
                                            url={parseCategoryUrl(category)}
                                            items={results}
                                            noTeaser
                                        />
                                    </Col>
                                )}
                            </Section>
                            <Divider />
                        </>
                    )}
                </>
            ))}
            <Section>
                <Content>
                    <Row>
                        <SectionTitle title="Wideo" url="/wideo" />{' '}
                        <VideoMainHitbox video={videos[0]} />
                    </Row>

                    <Row>
                        <Col span={24} style={{ paddingTop: 45 }}>
                            <CardGrid
                                items={videos.slice(1, -1)}
                                type="video"
                                url="/wideo"
                                maxItems={4}
                                showShowMore={false}
                                maxItemsInRowMobile={2}
                            />
                        </Col>
                    </Row>
                </Content>
                <Sider style={{ paddingTop: 45 }}>
                    <Row>
                        <Col span={24}>
                            <HalfPage id={ads.halfpage[3]} />
                        </Col>
                        <Col span={24}>
                            <Rectangle id={rectangle[6]} margin="45px 0 0 0" />
                        </Col>
                    </Row>
                </Sider>
            </Section>

            <Divider />
            {whitePapers && (
                <Row gutter={24}>
                    <Content>
                        <SectionTitle title="White Papers" url="/white-papers" />
                        <Row gutter={[16, 30]}>
                            <CardGrid type="white-paper" items={whitePapers} />
                        </Row>
                    </Content>
                    <Sider>
                        <Row justify="center">
                            <Col>
                                <HalfPage id={halfpage[4]} />
                            </Col>
                        </Row>
                    </Sider>
                </Row>
            )}
            <Row justify="center">
                <Content>
                    <ExpandNavibox id={navibox[2]} />
                </Content>
            </Row>
        </>
    );
};

export default CategoryPage;
